import React, { useMemo, useRef } from 'react';
import {
  mentionsSentimentFormatFuncMap,
  mentionsSentimentTab,
  MentionsSentimentTabType,
  TableHeight,
} from '../constants';
import { useCompareKeyMetricsChart } from '../hooks';
import { dealWithMentionsSentimentChart, getGameColorOrName } from '../utils';
import { echartsContent, sortMapAuxiliaryFunc } from '@/pages/opinion/utils';
import { formatTimeXAxisText } from '@op/utils/chart';
import useLanguage from '@op/hooks/useLanguage';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import { SortBy } from 'tea-component/lib/table/addons';
import './index.scss';
import TableColumn from '../component/TableColumn';
import { formatDateFunc } from '@op/utils/time';
import CardCommon from '@op/components/CardCommon';
import { getGameDataIndex } from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';
import ProgressColumn from '../component/ProgressColumn';
import { dealwithBrandHealth, getDefaultValue } from '@op/utils/value';

export default function MentionsSentimentCharts({
  filter,
  tab,
  chartType = 'line',
  gameData,
  setSortBy,
  sortBy,
}: {
  filter: any;
  tab: MentionsSentimentTabType;
  chartType?: string;
  gameData: {
    [id: string]: {
      name: string;
      color: string;
    };
  }[];
  setSortBy: React.Dispatch<React.SetStateAction<SortBy<any>>>;
  sortBy: SortBy<any>;
}) {
  const { formatMessage } = useLanguage();

  const chartMap = useMemo(() => {
    return mentionsSentimentTab.find((item) => item.value === tab);
  }, [tab]);
  const { text: tooltip, value: byType, formatData } = chartMap || {};
  const params = useMemo(
    () => ({
      ...filter,
      stats_types: ['mentions_sentiment'],
      tab_type: tab,
      by_type: byType,
    }),
    [byType, filter, tab],
  );

  const getGameData = getGameColorOrName(gameData);
  const formatFunc = mentionsSentimentFormatFuncMap[tab];
  const columns = useMemo(
    () => [
      {
        header: formatMessage({ id: 'Date' }),
        key: 'time',
        render: (cvm: any) => formatDateFunc(params?.date_type)(cvm.time),
        align: 'left',
      },
      {
        header: formatMessage({ id: 'Game' }),
        key: 'name',
        align: 'left',
        render: (cvm: any) => {
          const item: any = getGameData(cvm?.name, 'all');
          return <TableColumn name={item?.entity_name} item={item} />;
        },
      },
      {
        header: formatMessage({ id: tooltip }),
        key: 'value',
        align: 'center',
        render: (cvm: any) => (
          <ProgressColumn
            percentage={cvm.ratio}
            value={cvm.value}
            emptyValue={'-'}
            hideTrend={true}
            formateValue={formatFunc}
            oldTime={[]}
            newTime={[]}
            oldValue={''}
          ></ProgressColumn>
        ),
      },
    ],
    [formatData, formatMessage, params?.date_type, tooltip],
  );

  const { data: statistics, loading } = useCompareKeyMetricsChart({
    filter: params,
    dealWithToChart: dealWithMentionsSentimentChart,
  });

  const xAxisIntervalLimit = 8;
  const chartRef = useRef(null);
  const { xAxisData, yAxisData, tableData } = statistics;

  const [options, empty] = useMemo(() => {
    const hasStatistics = Array.isArray(xAxisData) && xAxisData.length > 0;
    if (!hasStatistics) {
      return [{}, !hasStatistics];
    }

    let legendData: {
      name: string;
      itemStyle: {
        color: string;
      };
    }[] = [];
    let seriesData: {
      itemStyle?: any;
      barMaxWidth?: string;
      barGap?: string;
      label?: any;
      name: string;
      type: string;
      stack?: string;
      data: (number | null)[];
    }[] = [];
    yAxisData.map((item: any) => {
      const color = getGameData(item.name, 'color');
      let data = item.data;
      if (tab === 'brand_health') {
        data = data.map((value: number) =>
          dealwithBrandHealth(getDefaultValue(value, 'brand_health')),
        );
      } else if (tab === 'sentiment') {
        data = data.map((item: number) => (item === 0 ? null : item));
      }

      seriesData.push({
        name: item.name,
        type: chartType,
        stack: chartType === 'bar' ? 'total' : '',
        data,
        barMaxWidth: '70px',
        itemStyle: { color },
      });

      legendData.push({
        name: item.name,
        itemStyle: { color },
      });
    });
    const sortMap = getGameDataIndex(gameData);
    legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
    const option = {
      tooltip: {
        trigger: 'axis',
        borderWidth: 0,
        padding: 0,
        confine: true,
        extraCssText: 'z-index:8',
        formatter: (data: any[]) => {
          const sumKey = data[0].axisValue;
          let timeText = '';
          if (params?.date_type) {
            timeText = formatDateFunc(params?.date_type)(sumKey);
          }
          return formatChartParamsDate({
            params: data,
            title: timeText,
            rightTitle: formatMessage({ id: tooltip }),
            listTitleFunc: (item) => getGameData(item.seriesName),
            valueFormat: (value) => {
              return formatFunc(value);
            },
            isSort: true,
          });
        },
      },
      legend: {
        data: legendData,
        formatter: (params: string) => {
          return getGameData(params);
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, params?.date_type),
      ),
      xAxis: {
        type: 'category',
        boundaryGap: ['20%', 0],
        data: xAxisData,
        axisLabel: {
          interval: xAxisData?.length > xAxisIntervalLimit ? 'auto' : 0,
          fontSize: 10,
          showMaxLabel: true,
          formatter: (value: any) =>
            formatTimeXAxisText(value, params?.date_type),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
          fontSize: 11,
        },
      },
      series: seriesData,
    };
    return [option, !hasStatistics];
  }, [
    yAxisData,
    xAxisData,
    chartType,
    params?.date_type,
    formatMessage,
    tooltip,
    formatData,
  ]);

  return (
    <div ref={chartRef} className="dbo-mention-sentiment-chart">
      <CardCommon
        contentCard
        loading={loading}
        loadingHeight={TableHeight.CHART_LOADING}
        empty={empty}
        emptyHeight={TableHeight.CHART_LOADING}
      >
        {chartType !== 'table' ? (
          <EChartCommon
            options={options}
            optionsProps={{ type: chartType }}
            loading={loading}
            leftTitle={formatMessage({ id: tooltip })}
            height={TableHeight.CHART}
          />
        ) : (
          <TableCommonHook
            recordKey={'edition_unified_id'}
            records={tableData || []}
            total={tableData?.length || 0}
            columns={columns}
            sortColumns={['time', 'value']}
            hasPageable={false}
            minWidth="auto"
            bordered="all"
            defaultSort={sortBy}
            changeSort={setSortBy}
            sortType={'local'}
            maxHeight={TableHeight.CHART_TABLE}
          />
        )}
      </CardCommon>
    </div>
  );
}
